import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import beastformsData from '../character/data/beastforms.json';
import { fuzzyFilter } from '../search/fuzzySearch';
import { useStrings } from '../locales';
import { SortIcon, SortState } from '../components/SortIcon';
import { StatBox, StatBrowser, StatFeature, StatItem } from '../components/StatBlock';
import './Beastforms.css';

// ── Types ─────────────────────────────────────────────────────────────
interface BeastFeature {
  name: string;
  text: string;
}

interface Beastform {
  name: string;
  tier: number;
  examples: string;
  trait?: string;
  traitMod?: number;
  evasionMod: number | null;
  attackRange?: string;
  attackTrait?: string;
  attackDamage?: string | null;
  attackMod?: number;
  advantages: string[];
  features: BeastFeature[];
}


interface FilterChip {
  label: string;
  color?: string;
  onRemove: () => void;
}

// ── Colour maps ─────────────────────────────────────────────────────
const TIER_COLORS: Record<number, string> = { 1: '#a89cc8', 2: '#60d890', 3: '#e8c547', 4: '#f06ab0' };

const TRAIT_COLORS: Record<string, string> = {
  Strength: '#ff7070',
  Agility: '#60d890',
  Finesse: '#f06ab0',
  Instinct: '#6aaeff',
  Presence: '#c47fff',
  Knowledge: '#e8c547',
};

const ALL_TIERS  = ['All', '1', '2', '3', '4'];
const ALL_TRAITS = ['All', 'Strength', 'Agility', 'Finesse', 'Instinct', 'Presence', 'Knowledge'];


const DEFAULT_SORT: SortState = { col: '', dir: 'asc' };

// ── Multi-select helpers ─────────────────────────────────────────────
function toggleFilter(current: Set<string>, value: string): Set<string> {
  if (value === 'All') return new Set(['All']);
  const next = new Set(current);
  next.delete('All');
  if (next.has(value)) {
    next.delete(value);
    if (next.size === 0) next.add('All');
  } else {
    next.add(value);
  }
  return next;
}
function passesFilter(set: Set<string>, value: string): boolean { return set.has('All') || set.has(value); }
function isDefault(s: Set<string>): boolean { return s.size === 1 && s.has('All'); }

// ── Sort helpers ─────────────────────────────────────────────────────
function parseDamageFace(dmg: string | null | undefined): number {
  if (!dmg) return 0;
  const m = dmg.match(/d(\d+)/i);
  return m ? parseInt(m[1], 10) : 0;
}

function sortBeastforms(list: Beastform[], sort: SortState): Beastform[] {
  if (!sort.col) return list;
  const d = sort.dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    switch (sort.col) {
      case 'tier':    return d * (a.tier - b.tier) || a.name.localeCompare(b.name);
      case 'name':    return d * a.name.localeCompare(b.name);
      case 'trait':   return d * (a.trait || '').localeCompare(b.trait || '');
      case 'damage':  return d * (parseDamageFace(a.attackDamage) + (a.attackMod || 0) - parseDamageFace(b.attackDamage) - (b.attackMod || 0));
      case 'evasion': return d * ((a.evasionMod || 0) - (b.evasionMod || 0));
      default:        return 0;
    }
  });
}

// ── Sort icon ────────────────────────────────────────────────────────

// ── Reset button ─────────────────────────────────────────────────────
interface ResetBtnProps { dirty: boolean; onClick: () => void; }
function ResetBtn({ dirty, onClick }: ResetBtnProps) {
  return (
    <button className={`reset-btn${dirty ? ' dirty' : ''}`} onClick={onClick} disabled={!dirty} title="Reset filters">
      <RotateCcw size={13} /> Reset
    </button>
  );
}

// ── Stat card, laid out like the SRD beastform block ────────────────
function attackOf(bf: Beastform): string | null {
  if (bf.attackDamage === null || bf.attackDamage === undefined) return null;
  const mod = bf.attackMod ?? 0;
  return `${bf.attackRange} ${bf.attackTrait} ${bf.attackDamage}${mod > 0 ? `+${mod}` : mod < 0 ? mod : ''} phy`;
}

function BeastStatCard({ bf }: { bf: Beastform }) {
  const s = useStrings().beastforms;
  const attack = attackOf(bf);
  const stats: StatItem[] = [
    ...(bf.trait ? [{ value: `${bf.trait} +${bf.traitMod}` }] : []),
    ...(bf.evasionMod !== null ? [{ value: `${s.sortCols.evasion} +${bf.evasionMod}` }] : []),
    ...(attack ? [{ value: attack }] : []),
  ];
  return (
    <article className="srdcard">
      <h2 className="srdcard__name">{bf.name}</h2>
      <p className="srdcard__kind">{`Tier ${bf.tier} (${bf.examples})`}</p>
      {(stats.length > 0 || bf.advantages.length > 0) && (
        <StatBox
          rows={stats.length > 0 ? [stats] : []}
          footer={bf.advantages.length > 0 && <><strong>{s.advantageLabel}</strong> {bf.advantages.join(', ')}</>}
        />
      )}
      <h3 className="srdcard__features">{s.featuresTitle}</h3>
      {bf.features.map((f, i) => <StatFeature key={i} feature={{ name: f.name, text: f.text }} />)}
    </article>
  );
}

// ════════════════════════════════════════════════════════════════
//   Main page
// ════════════════════════════════════════════════════════════════
export default function Beastforms() {
  const s = useStrings().beastforms;
  const SORT_COLS_BF = [
    { col: 'tier',    label: s.sortCols.tier },
    { col: 'name',    label: s.sortCols.name },
    { col: 'trait',   label: s.sortCols.trait },
    { col: 'damage',  label: s.sortCols.damage },
    { col: 'evasion', label: s.sortCols.evasion },
  ];
  const [searchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [tierFilter,  setTierFilter]  = useState<Set<string>>(new Set(['All']));
  const [traitFilter, setTraitFilter] = useState<Set<string>>(new Set(['All']));
  const [search, setSearch]           = useState<string>(searchParams.get('q') || '');
  const [sort, setSort]               = useState<SortState>(DEFAULT_SORT);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) setSearch(q);
  }, [searchParams]);

  const handleSort = (col: string) =>
    setSort((prev) => prev.col === col
      ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { col, dir: 'asc' }
    );

  const isSortDirty = sort.col !== '';
  const dirty = !!search || !isDefault(tierFilter) || !isDefault(traitFilter) || isSortDirty;

  const reset = () => {
    setSearch(''); setTierFilter(new Set(['All']));
    setTraitFilter(new Set(['All'])); setSort(DEFAULT_SORT);
  };

  const filtered = useMemo(() => {
    const base = (beastformsData as Beastform[]).filter((b) =>
      passesFilter(tierFilter, String(b.tier)) &&
      (traitFilter.has('All') || passesFilter(traitFilter, b.trait || ''))
    );
    return fuzzyFilter(base, search,
      (b) => [b.examples, ...(b.features || []).map((f) => f.name + ' ' + f.text), ...(b.advantages || [])].join(' '),
      (b) => b.name
    );
  }, [tierFilter, traitFilter, search]);

  // When searching, keep fuzzy relevance order; otherwise apply column sort
  const sorted = useMemo(
    () => search.trim() ? filtered : sortBeastforms(filtered, sort),
    [filtered, sort, search]
  );
  const useTierGroups = !search.trim() && (sort.col === '' || (sort.col === 'tier' && sort.dir === 'asc'));


  const chips: FilterChip[] = [
    ...(search ? [{ label: `"${search}"`, color: 'var(--gold)', onRemove: () => setSearch('') }] : []),
    ...[...traitFilter].filter(v => v !== 'All').map(v => ({ label: v, color: TRAIT_COLORS[v], onRemove: () => setTraitFilter(p => toggleFilter(p, v)) })),
    ...[...tierFilter].filter(v => v !== 'All').map(v => ({ label: `T${v}`, color: TIER_COLORS[Number(v)], onRemove: () => setTierFilter(p => toggleFilter(p, v)) })),
  ];

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>{s.title}</h1>
        <p>{s.desc}</p>
      </div>

      {/* Rules info box */}
      <div className="bf-rules-box">
        <h3 className="bf-rules-box__title">Beastform Rules</h3>
        <div className="bf-rules-box__grid">
          {[
            { k: 'Transforming', v: 'Mark a Stress to transform into a Beastform of your tier or lower. You return to your normal form when you choose or fall unconscious.' },
            { k: 'Creature Category', v: "Each category's name describes the common role or behavior of creatures in that category (such as Agile Scout). This name is followed by a few examples of animals that fit in that category." },
            { k: 'Character Trait', v: 'While transformed, you gain a bonus to the listed trait. When this form drops, you lose this bonus.' },
            { k: 'Attack Rolls', v: 'When you make an attack while transformed, you use the creature\'s listed range, trait, and damage dice, but you use your Proficiency.' },
            { k: 'Evasion', v: 'While transformed, you add the creature\'s Evasion bonus to your normal Evasion.' },
            { k: 'Advantages', v: 'Your form makes you especially suited to certain actions. When you make an action or reaction roll related to one of the verbs listed, you gain advantage on that roll.' },
            { k: 'Features', v: 'Each form includes unique features that reflect its nature and strengths.' },
          ].map(({ k, v }) => (
            <div key={k} className="bf-rules-box__rule">
              <strong>{k}:</strong> {v}
            </div>
          ))}
        </div>
      </div>

      <input
        type="text"
        id="beastforms-search"
        name="beastforms-search"
        aria-label={s.searchAria}
        placeholder={s.searchPlaceholder}
        value={search}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
        className="search-input filter-search-bar"
      />

      <div className="filter-layout">

        {/* Mobile: toggle + reset row */}
        <div className="filter-toggle-row">
          <button
            className="filter-toggle-btn"
            onClick={() => setShowFilters(f => !f)}
            aria-expanded={showFilters}
            aria-controls="filter-panel"
          >
            <span>⚙ Filters{dirty ? ' •' : ''}</span>
            <span>{showFilters ? '▲' : '▼'}</span>
          </button>
          <ResetBtn dirty={dirty} onClick={reset} />
        </div>

        {/* Mobile: active filter chips */}
        {chips.length > 0 && (
          <div className="mobile-filter-chips">
            {chips.map(({ label, color, onRemove }) => (
              <span key={label} className="mobile-filter-chip" style={{ '--chip-color': color || 'var(--gold)' } as React.CSSProperties}>
                {label}
                <button className="mobile-filter-chip__remove" onClick={onRemove}>×</button>
              </span>
            ))}
          </div>
        )}

        {/* ── Left panel ── */}
        <div id="filter-panel" className={`filter-sidebar filter-sidebar-panel${showFilters ? '' : ' filter-sidebar--hidden'}`}>
          <div className="filter-sidebar-panel__header">
            <span className="filter-label">Filters</span>
            <span className="hide-mobile"><ResetBtn dirty={dirty} onClick={reset} /></span>
          </div>

          <div className="filter-sidebar-panel__count" aria-live="polite" aria-atomic="true">{filtered.length} of {beastformsData.length}</div>

          <div className="filter-sidebar-panel__section">
            <div className="filter-label filter-sidebar-panel__section-label">Trait</div>
            <div className="filter-sidebar-panel__pill-group">
              {ALL_TRAITS.map((t) => {
                const active = traitFilter.has(t);
                const accent = t !== 'All' ? TRAIT_COLORS[t] : null;
                return (
                  <button
                    key={t}
                    onClick={() => setTraitFilter(toggleFilter(traitFilter, t))}
                    className={`sidebar-filter-btn${active ? ' active' : ''}`}
                    aria-pressed={active}
                    style={accent ? {
                      '--role-color':      accent,
                      '--role-bg':         `${accent}22`,
                      '--role-border':     accent,
                      '--role-color-dim':  `${accent}cc`,
                      '--role-border-dim': `${accent}44`,
                    } as React.CSSProperties : {}}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

        </div>

        {/* ── Right panel ── */}
        <div className="filter-right-panel">

          <div className="horiz-filter-bar">
            <div className="horiz-filter-group">
              <span className="filter-label">Tier</span>
              {ALL_TIERS.map((t) => (
                <button
                  key={t}
                  className={`filter-btn horiz-filter-btn${tierFilter.has(t) ? ' active' : ''}`}
                  aria-pressed={tierFilter.has(t)}
                  onClick={() => setTierFilter(toggleFilter(tierFilter, t))}
                >
                  {t === 'All' ? 'All' : `T${t}`}
                </button>
              ))}
            </div>
            <div className="horiz-filter-divider" />
            <div className="horiz-filter-group">
              <span className="filter-label">Sort</span>
              {SORT_COLS_BF.map(({ col, label }) => (
                <button
                  key={col}
                  className={`filter-btn horiz-filter-btn${sort.col === col ? ' active' : ''}`}
                  onClick={() => handleSort(col)}
                >
                  {label}<SortIcon col={col} sort={sort} />
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 && <div className="empty-state" role="status"><p>{s.noMatch}</p></div>}

          <StatBrowser
            groups={useTierGroups
              ? [1, 2, 3, 4].map((tier) => {
                const items = sorted.filter((b) => b.tier === tier);
                return { key: `tier-${tier}`, label: <><span>Tier {tier}</span><span>{items.length}</span></>, items };
              })
              : [{ key: 'all', items: sorted }]}
            keyOf={(bf) => bf.name}
            accentOf={(bf) => (bf.trait ? TRAIT_COLORS[bf.trait] : undefined) || TIER_COLORS[bf.tier] || 'var(--border-light)'}
            renderItem={(bf) => (
              <>
                <span className="statview__name">{bf.name}</span>
                <span className="statview__meta">{bf.examples}</span>
              </>
            )}
            renderCard={(bf) => <BeastStatCard bf={bf} />}
            listLabel={s.title}
            closeLabel={s.backToList}
          />
        </div>
      </div>
    </div>
  );
}
